import {
  STATUS_BADGES,
  type StatusBadgeSpec,
} from "@/shared/components/StatusBadge";
import {
  type SessionIngestionCounts,
  type SessionIngestionSummary,
} from "@/shared/domain/sessions";

/** The ingestion badge beside a session's status. */
export interface SessionBadge {
  /** How it is drawn: an entry of `STATUS_BADGES.sessionIngestion`. */
  status: StatusBadgeSpec;
  /** What it says: the status's label, or a fuller one in its place. */
  label: string;
  /**
   * Set while the ingestion isn't known: "checking" until it is, "unknown" if
   * it couldn't be loaded.
   */
  placeholder?: "checking" | "unknown";
}

const { sessionIngestion } = STATUS_BADGES;

function badge(status: StatusBadgeSpec, label = status.label): SessionBadge {
  return { status, label };
}

/** A complete session's ingestion while it isn't known yet. */
export const CHECKING_BADGE: SessionBadge = {
  ...badge(sessionIngestion.checking),
  placeholder: "checking",
};

/** A complete session's ingestion when it couldn't be loaded. */
export const UNKNOWN_BADGE: SessionBadge = {
  ...badge(sessionIngestion.unknown),
  placeholder: "unknown",
};

/** A session's file counts and the state they were read in. */
export type SessionIngestionCountsWithState = SessionIngestionCounts &
  Pick<SessionIngestionSummary, "state">;

/**
 * The ingestion badge the session page and the sessions list both show, so
 * they always say the same thing. Until the counts load, a complete session
 * says it's checking, or that its status is unknown if they failed to load;
 * an in-progress one shows nothing.
 */
export function ingestionBadgeFor(
  complete: boolean,
  counts: SessionIngestionCountsWithState | undefined,
  { failed }: { failed: boolean },
): SessionBadge | null {
  if (counts !== undefined) {
    return sessionIngestionBadge(complete, counts);
  }
  if (!complete) {
    return null;
  }
  return failed ? UNKNOWN_BADGE : CHECKING_BADGE;
}

/**
 * The badge beside a session's status saying whether its data is ready, in
 * the words file ingestion labels use.
 *
 * A complete session whose counts were read while it was still in progress
 * shows it's checking. Null for an in-progress session with no file waiting,
 * and for a complete session with nothing to ingest: no file ingested and
 * none waiting.
 */
function sessionIngestionBadge(
  complete: boolean,
  counts: SessionIngestionCountsWithState,
): SessionBadge | null {
  if (complete && counts.state === "in_progress") {
    return CHECKING_BADGE;
  }
  if (counts.pending_file_count > 0) {
    return badge(
      counts.failed_file_count > 0
        ? sessionIngestion.failed
        : sessionIngestion.ingesting,
    );
  }
  if (!complete || counts.ingested_file_count === 0) {
    return null;
  }
  const skipped = counts.skipped_file_count;
  return skipped > 0
    ? badge(
        sessionIngestion.ingested,
        `${sessionIngestion.ingested.label} · ${skipped} not waited for`,
      )
    : badge(sessionIngestion.ingested);
}
